"use client"

import { Toaster as Sonner } from "sonner"

type ToasterProps = React.ComponentProps<typeof Sonner>

/**
 * App-wide Sonner toaster. The default position is `bottom-right`
 * (Sonner's library default), which keeps every existing transactional
 * toast (`toast.success(...)`, `toast.error(...)`, `toast.info(...)`,
 * undo banners, etc.) anchored at the bottom-right of the viewport.
 *
 * Realtime in-app toasts (chat previews + notification previews) opt
 * into the top-center surface by passing `position: "top-center"` per
 * call to `toast.custom(...)`. Sonner adds a second `<ol>` for the
 * top-center position only when at least one such toast is in flight,
 * so existing call sites do not need any changes.
 *
 * We deliberately mount a single `<Toaster>` instance: when multiple
 * Toaster instances are mounted, every position-less toast renders in
 * each of them (each Toaster treats its own default as index 0 of its
 * position array), which would double-render every existing
 * transactional toast.
 */
const REALTIME_TOAST_BASE_CLASS =
  "group realtime-toast group-[.toaster]:rounded-2xl group-[.toaster]:p-0 group-[.toaster]:shadow-xl group-[.toaster]:bg-eoe-bg-modal dark:group-[.toaster]:bg-eoe-dark-bg-modal";

const Toaster = ({ ...props }: ToasterProps) => {

  return (
    <Sonner
      className="toaster group"
      theme="system"
      closeButton={true}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-eoe-bg-modal dark:group-[.toaster]:bg-eoe-dark-bg-modal group-[.toaster]:text-eoe-text dark:group-[.toaster]:text-eoe-dark-text group-[.toaster]:border-none group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-eoe-text-sub dark:group-[.toast]:text-eoe-dark-text-sub",
          actionButton:
            "group-[.toast]:bg-eoe-bg-hover dark:group-[.toast]:bg-eoe-dark-bg-hover group-[.toast]:text-eoe-text-sub dark:group-[.toast]:text-eoe-dark-text-sub",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          closeButton:
            "group-[.toast]:bg-eoe-bg-hover dark:group-[.toast]:bg-eoe-dark-bg-hover group-[.toast]:text-eoe-text-sub dark:group-[.toast]:text-eoe-dark-text-sub",
        },
        duration: 1800,
      }}

      {...props}
    />
  )
}

/**
 * Default options applied to every realtime in-app toast (chat preview
 * or notification preview). Exposed so consumers (the
 * `useRealtimeToasts` hook) opt in via `toast.custom(jsx, REALTIME_TOAST_OPTIONS)`
 * and we do not have to repeat duration / position / sizing per call
 * site. Keeping these centralized in the same file as the `<Toaster>`
 * declaration prevents the two from drifting.
 *
 * `position: "top-center"` is what causes Sonner to render the toast in
 * the top-center `<ol>` instead of the bottom-right default.
 */
const REALTIME_TOAST_OPTIONS = {
  position: "top-center" as const,
  duration: 6000,
  closeButton: false,
  className: REALTIME_TOAST_BASE_CLASS,
  style: {
    width: "min(420px, 92vw)",
    padding: 0,
  },
} as const;

export { Toaster, REALTIME_TOAST_OPTIONS }
